import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import {
  FaHome,
  FaUserFriends,
  FaCalendarAlt,
  FaTasks,
  FaStar,
  FaTicketAlt,
  FaSignOutAlt,
  FaPlug,
  FaBuilding,
  FaAddressBook,
  FaFileInvoiceDollar,
  FaExchangeAlt,
  FaCog,
  FaServer,
  FaRegCalendarAlt,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from './context/AuthContext';
import { useUserRole } from './hooks/useUserRole';
import { useBilling } from './context/BillingContext';
import NotificationCenter from './components/NotificationCenter';
import TenantSelectionModal, { Tenant } from './components/TenantSelectionModal';

declare const __APP_VERSION__: string;

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick?: (e: React.MouseEvent) => void;
  testId?: string;
  disabled?: boolean;
  disabledReason?: string;
  addonLocked?: boolean;
}

function NavItem({ to, icon, label, onClick, testId, disabled, disabledReason, addonLocked }: NavItemProps): React.JSX.Element {
  const [showTooltip, setShowTooltip] = useState(false);
  const navigate = useNavigate();

  const tooltipContent = addonLocked ? (
    <>
      {label}
      <div style={{ fontSize: '10px', color: '#E7007D', marginTop: '2px', fontWeight: 600 }}>
        Modul freischalten
      </div>
    </>
  ) : (
    <>{label}{disabled ? ` (${disabledReason || 'No access'})` : ''}</>
  );

  const tooltipNode = showTooltip && (
    <div
      style={{
        position: 'absolute',
        left: '100%',
        top: '50%',
        transform: 'translateY(-50%)',
        marginLeft: '8px',
        background: '#1e293b',
        color: 'white',
        padding: '6px 12px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        zIndex: 1000,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        pointerEvents: 'none',
      }}
    >
      {tooltipContent}
      <div
        style={{
          position: 'absolute',
          left: '-4px',
          top: '50%',
          transform: 'translateY(-50%) rotate(45deg)',
          width: '8px',
          height: '8px',
          background: '#1e293b',
        }}
      />
    </div>
  );

  if (addonLocked) {
    return (
      <li style={{ position: 'relative' }}>
        <div
          className="sidebar-link"
          data-testid={testId}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px',
            borderRadius: '8px',
            opacity: 0.5,
            cursor: 'pointer',
          }}
          onClick={() => navigate('/settings/billing')}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {icon}
        </div>
        {tooltipNode}
      </li>
    );
  }

  if (disabled) {
    return (
      <li style={{ position: 'relative' }}>
        <div
          className="sidebar-link"
          data-testid={testId}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px',
            borderRadius: '8px',
            opacity: 0.35,
            cursor: 'not-allowed',
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {icon}
        </div>
        {tooltipNode}
      </li>
    );
  }

  return (
    <li style={{ position: 'relative' }}>
      <NavLink
        to={to}
        className={({ isActive }) =>
          isActive ? 'sidebar-link sidebar-link-active' : 'sidebar-link'
        }
        onClick={onClick}
        data-testid={testId}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px',
          borderRadius: '8px',
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {icon}
      </NavLink>
      {tooltipNode}
    </li>
  );
}

const MANAGER_ALLOWED_PAGES = ['/home', '/employees', '/shiftplan', '/organization'];

function Sidebar(): React.JSX.Element {
  const { isAdmin } = useUserRole();
  const { hasAddon } = useBilling();
  const { isPlatformAdmin } = useAuthContext();
  const settingsMenuRef = useRef<HTMLLIElement>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState<boolean>(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState<boolean>(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(e.target as Node)) {
        setShowSettingsMenu(false);
      }
    };
    if (showSettingsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettingsMenu]);
  const [showTenantDialog, setShowTenantDialog] = useState<boolean>(false);
  const [showTenantTooltip, setShowTenantTooltip] = useState<boolean>(false);
  const { logout, selectedTenant, user, selectTenant } = useAuthContext();
  const navigate = useNavigate();

  const handleLogout = (): void => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = (): void => {
    logout();
    window.location.href = '/login';
  };

  const cancelLogout = (): void => {
    setShowLogoutConfirm(false);
  };

  const handleTenantLabelClick = (): void => {
    setShowTenantDialog(true);
  };

  const handleTenantSelect = (t: Tenant): void => {
    selectTenant(t);
    window.location.reload();
  };

  const handleCloseTenantDialog = (): void => {
    setShowTenantDialog(false);
  };

  const tenantInitials = selectedTenant?.tenant_name
    ? selectedTenant.tenant_name.substring(0, 2).toUpperCase()
    : 'T';

  return (
    <div
      className="sidebar"
      style={{
        position: 'relative',
        height: '100%',
        width: '72px',
        minWidth: '72px',
        maxWidth: '72px',
        padding: '12px 8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxSizing: 'border-box',
      }}
    >
      {/* Tenant Button */}
      {selectedTenant && (
        <div
          style={{ position: 'relative', marginBottom: '16px' }}
          onMouseEnter={() => setShowTenantTooltip(true)}
          onMouseLeave={() => setShowTenantTooltip(false)}
        >
          <div
            onClick={handleTenantLabelClick}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #5271A5 0%, #3d5a8a 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '14px',
              color: 'white',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(82, 113, 165, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {tenantInitials}
          </div>
          {showTenantTooltip && (
            <div
              style={{
                position: 'absolute',
                left: '100%',
                top: '50%',
                transform: 'translateY(-50%)',
                marginLeft: '8px',
                background: '#1e293b',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                pointerEvents: 'none',
              }}
            >
              {selectedTenant.tenant_name}
              <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '2px' }}>
                <FaExchangeAlt style={{ fontSize: '8px', marginRight: '4px' }} />
                Switch Tenant
              </div>
              <div
                style={{
                  position: 'absolute',
                  left: '-4px',
                  top: '50%',
                  transform: 'translateY(-50%) rotate(45deg)',
                  width: '8px',
                  height: '8px',
                  background: '#1e293b',
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Divider */}
      <div
        style={{
          width: '32px',
          height: '1px',
          background: 'rgba(255, 255, 255, 0.15)',
          marginBottom: '12px',
        }}
      />

      {/* Navigation */}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, width: '100%' }}>
        {isPlatformAdmin && (
          <NavItem to="/platform" icon={<FaServer className="sidebar-icon" />} label="Platform" />
        )}
        {[
          { to: '/home', icon: <FaHome className="sidebar-icon" />, label: 'Home' },
          { to: '/employees', icon: <FaUserFriends className="sidebar-icon" />, label: 'Employees' },
          { to: '/shiftplan', icon: <FaCalendarAlt className="sidebar-icon" />, label: 'Shiftplan' },
          { to: '/events', icon: <FaRegCalendarAlt className="sidebar-icon" />, label: 'Events' },
          { to: '/organization', icon: <FaBuilding className="sidebar-icon" />, label: 'Organization' },
          { to: '/contacts', icon: <FaAddressBook className="sidebar-icon" />, label: 'Contacts', testId: 'nav-contacts' },
          { to: '/sales', icon: <FaFileInvoiceDollar className="sidebar-icon" />, label: 'Sales', requiredAddon: 'documents' as const },
          { to: '/operations', icon: <FaTasks className="sidebar-icon" />, label: 'Operations' },
          { to: '/tickets', icon: <FaTicketAlt className="sidebar-icon" />, label: 'Tickets' },
          { to: '/reviews', icon: <FaStar className="sidebar-icon" />, label: 'Reviews' },
          { to: '/settings/integrations', icon: <FaPlug className="sidebar-icon" />, label: 'Integrations' },
        ].map((item) => {
            const addonLocked = item.requiredAddon != null && !hasAddon(item.requiredAddon);
            const roleDisabled = !isAdmin && !MANAGER_ALLOWED_PAGES.includes(item.to);
            const disabled = roleDisabled;
            return <NavItem key={item.to} to={item.to} icon={item.icon} label={item.label} testId={item.testId} disabled={disabled} disabledReason="No access" addonLocked={addonLocked} />;
          })}
        {/* Notification Center (admin only) */}
        {isAdmin && (
          <li style={{ display: 'flex', justifyContent: 'center' }}>
            <NotificationCenter />
          </li>
        )}
        <li ref={settingsMenuRef} style={{ position: 'relative' }}>
          <div
            className="sidebar-link"
            onClick={() => isAdmin ? setShowSettingsMenu((prev) => !prev) : handleLogout()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            {isAdmin ? <FaCog className="sidebar-icon" /> : <FaSignOutAlt className="sidebar-icon" />}
          </div>
          {showSettingsMenu && (
            <div
              style={{
                position: 'absolute',
                left: '100%',
                bottom: 0,
                marginLeft: '8px',
                background: '#1e293b',
                borderRadius: '8px',
                padding: '6px 0',
                minWidth: '160px',
                zIndex: 1000,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: '-4px',
                  bottom: '14px',
                  transform: 'rotate(45deg)',
                  width: '8px',
                  height: '8px',
                  background: '#1e293b',
                }}
              />
              <button
                onClick={() => {
                  setShowSettingsMenu(false);
                  navigate('/settings/billing');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '10px 16px',
                  border: 'none',
                  background: 'transparent',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <FaCog size={12} style={{ opacity: 0.7 }} />
                Settings
              </button>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '2px 12px' }} />
              <button
                onClick={() => {
                  setShowSettingsMenu(false);
                  handleLogout();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '10px 16px',
                  border: 'none',
                  background: 'transparent',
                  color: '#f87171',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <FaSignOutAlt size={12} style={{ opacity: 0.7 }} />
                Logout
              </button>
            </div>
          )}
        </li>
      </ul>

      {/* Logo */}
      <div
        style={{
          position: 'absolute',
          bottom: '45px',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <img
          src="/img/admin_desk_logo_heller.svg"
          alt="Sundae OS Logo"
          style={{ width: '36px', opacity: 0.8 }}
        />
      </div>

      {/* Version */}
      <div
        style={{
          position: 'absolute',
          bottom: '12px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '9px',
          color: '#6c757d',
          whiteSpace: 'nowrap',
        }}
      >
        v{__APP_VERSION__}
      </div>

      {/* Logout Dialog - portaled to body to escape backdrop-filter containing block */}
      {showLogoutConfirm && createPortal(
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.3)',
              zIndex: 1000,
            }}
            onClick={cancelLogout}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
              zIndex: 1001,
              minWidth: '280px',
            }}
          >
            <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#1e293b' }}>
              Confirm Logout
            </h4>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#64748b' }}>
              Are you sure you want to logout?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={cancelLogout}
                style={{
                  padding: '8px 16px',
                  background: '#e2e8f0',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                style={{
                  padding: '8px 16px',
                  background: '#0E1333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Tenant Selection Modal - portaled to body to escape backdrop-filter containing block */}
      {showTenantDialog && createPortal(
        <TenantSelectionModal
          tenants={user?.tenant_list}
          onSelect={handleTenantSelect}
          onClose={handleCloseTenantDialog}
        />,
        document.body
      )}
    </div>
  );
}

export default Sidebar;
